import React, { useEffect, useState } from "react";
import {
  InputAdornment,
  TextField as MuiTextField,
  TextFieldProps,
} from "@mui/material";
import {
  FieldTitle,
  FormDataConsumer,
  useDataProvider,
  useInput,
  InputProps,
  Identifier,
  InputHelperText,
} from "react-admin";

type EmailInputProps = InputProps<TextFieldProps> & {
  domainIdField?: string;
  allowEmptyLocalPart?: boolean;
  helperText?: string;
  fullWidth?: boolean;
};

export default function EmailInput({
  source,
  validate,
  domainIdField = "domainId",
  allowEmptyLocalPart = false,
  ...inputProps
}: EmailInputProps) {
  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <CombinedInput
          source={source}
          validate={validate}
          domainId={formData[domainIdField]}
          allowEmptyLocalPart={allowEmptyLocalPart}
          {...inputProps}
          {...rest}
        />
      )}
    </FormDataConsumer>
  );
}

// TODO Set id

type CombinedInputProps = InputProps & {
  domainId: Identifier;
  helperText?: string;
  allowEmptyLocalPart: boolean;
} & InputProps<TextFieldProps>;

const CombinedInput = ({
  domainId,
  helperText,
  label,
  allowEmptyLocalPart,
  ...props
}: CombinedInputProps) => {
  const [domainName, setDomainName] = useState("");
  const [realDomainName, setRealDomainName] = useState("");

  const { onChange, onBlur, ...rest } = props;

  const {
    id,
    isRequired,
    field,
    fieldState: { isTouched, error },
    formState: { isSubmitted },
  } = useInput({
    format: (value = "") => value.split("@")[0],
    parse: (value = "") => value + "@" + realDomainName,
    // Pass the event handlers to the hook but not the component as the field property already has them.
    // useInput will call the provided onChange and onBlur in addition to the default needed by react-hook-form.
    onChange,
    onBlur,
    ...props,
  });

  const dataProvider = useDataProvider();

  useEffect(() => {
    if (!domainId) {
      return;
    }

    dataProvider.getOne("Domain", { id: domainId }).then(({ data }) => {
      setDomainName(data.unicodeName);
      setRealDomainName(data.domain);
    });
  }, [domainId, dataProvider]);

  useEffect(() => {
    // Explicitly trigger a change to the input to invoke the parse function on domain name changes
    field.onChange(field.value);

    // Adding value as a dependency could cause an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainName, realDomainName]);

  return (
    <MuiTextField
      id={id}
      {...field}
      {...rest}
      disabled={!domainName}
      variant="filled"
      margin="dense"
      autoComplete="off"
      label={
        label !== "" &&
        label !== false && (
          <FieldTitle
            label={label}
            source={props.source}
            resource={props.resource}
            isRequired={isRequired && !allowEmptyLocalPart}
          />
        )
      }
      error={(isTouched || isSubmitted) && Boolean(error)}
      helperText={
        <InputHelperText
          touched={isTouched || isSubmitted}
          error={error?.message}
          helperText={helperText}
        />
      }
      onPaste={(event) => {
        // Fix #2 by splitting the local part and preventing default on paste
        event.preventDefault();

        const value = event.clipboardData.getData("text/plain");
        const localPart = value.split("@")[0];
        const trimmedLocalPart = localPart.trim();

        field.onChange(trimmedLocalPart);
      }}
      InputProps={{
        endAdornment: domainName && (
          <InputAdornment position="end">@{domainName}</InputAdornment>
        ),
      }}
    />
  );
};
